/**
 * Charts — Chart.js lifecycle, the ISG palette, and presets.
 *
 * Chart.js 4 is the estate standard (isg-bi, isg-marketing, isg-sales,
 * isg-vault). This module was `components/chart-helpers.js`, duplicated in
 * marketing AND sales — and the copies had already drifted: marketing gained
 * `LogarithmicScale`, the annotation plugin and `cssVar`; sales had none of
 * them. The superset lives here now.
 *
 * ⚠️ **Chart.js throws if a canvas already has a chart on it.** In an SPA
 * that re-renders a page, that is not an edge case, it is every navigation —
 * which is why `createChart` destroys the previous instance for that canvas
 * and why `destroyAllCharts()` must be called when a page unmounts.
 *
 * ⚠️ **Green / amber / red are reserved for STATE** — pass/fail, SLA health,
 * breach. Do not use them for an ordinary data series, or a chart of two
 * neutral categories reads as "one of these is bad". `PALETTE` is ordered so
 * the first colours picked are neutral.
 *
 * isg-helpdesk's /reports hand-rolls SVG instead; it predates this and is on
 * the IT team's daily path, so migrating it is a separate change.
 *
 *   import { createChart, withPreset, PALETTE, destroyAllCharts } from '@isg/ui-kit';
 */
import {
  Chart, ArcElement, BarElement, LineElement, PointElement,
  CategoryScale, LinearScale, LogarithmicScale, TimeScale,
  Title, Tooltip, Legend, Filler,
  DoughnutController, BarController, LineController,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import annotationPlugin from 'chartjs-plugin-annotation';
import 'chartjs-adapter-date-fns';

// Register all needed components once
Chart.register(
  ArcElement, BarElement, LineElement, PointElement,
  CategoryScale, LinearScale, LogarithmicScale, TimeScale,
  Title, Tooltip, Legend, Filler,
  DoughnutController, BarController, LineController,
  ChartDataLabels, annotationPlugin,
);

// Track instances by canvas ID for cleanup
const chartInstances = new Map();

/**
 * Create or replace a Chart.js chart on a canvas element.
 * Automatically destroys any previous chart on the same canvas.
 */
export function createChart(canvas, config) {
  const id = canvas.id || canvas;
  const el = typeof canvas === 'string' ? document.getElementById(canvas) : canvas;
  if (!el) return null;

  // Destroy previous instance
  if (chartInstances.has(id)) {
    chartInstances.get(id).destroy();
    chartInstances.delete(id);
  }

  // Default: disable datalabels unless explicitly enabled
  if (!config.options) config.options = {};
  if (!config.options.plugins) config.options.plugins = {};
  if (config.options.plugins.datalabels === undefined) {
    config.options.plugins.datalabels = false;
  }

  const chart = new Chart(el, config);
  chartInstances.set(id, chart);
  return chart;
}

/**
 * The live Chart instance for a canvas, or null.
 *
 * Chart.js is a module import here, not a global, so `Chart.getChart` is not
 * reachable from a console or a browser test — and the instance registry
 * below is private. This is the supported way to reach a chart after
 * creating it, for a live update or to assert on it from a test.
 */
export function getChart(canvas) {
  const id = typeof canvas === 'string' ? canvas : (canvas && canvas.id);
  return chartInstances.get(id) || null;
}

/**
 * Destroy all tracked chart instances (call on page unmount).
 */
export function destroyAllCharts() {
  chartInstances.forEach(c => c.destroy());
  chartInstances.clear();
}

/**
 * Resolve a CSS variable to its computed value (Chart.js needs raw hex/rgb).
 */
function cssVar(name) {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

/**
 * ISG-branded colour palette for charts.
 */
export const CHART_COLORS = {
  brand:   '#006699',
  accent:  '#BD1B44',
  green:   '#16a34a',
  red:     '#dc2626',
  amber:   '#d97706',
  blue:    '#3b82f6',
  purple:  '#7c3aed',
  gray:    '#6b7280',
  teal:    '#0d9488',
  orange:  '#ea580c',
};

/**
 * Palette array for multi-series charts.
 */
export const PALETTE = [
  CHART_COLORS.brand, CHART_COLORS.blue, CHART_COLORS.green,
  CHART_COLORS.amber, CHART_COLORS.accent, CHART_COLORS.purple,
  CHART_COLORS.teal, CHART_COLORS.orange, CHART_COLORS.gray, CHART_COLORS.red,
];

/**
 * Common config presets.
 */
export const PRESETS = {
  donut: {
    type: 'doughnut',
    options: {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '60%',
      plugins: {
        legend: { position: 'bottom', labels: { padding: 16, usePointStyle: true, font: { size: 12 } } },
        datalabels: {
          color: '#fff',
          font: { weight: 'bold', size: 12 },
          formatter: (v) => v > 0 ? v : '',
        },
      },
    },
  },

  horizontalBar: {
    type: 'bar',
    options: {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        datalabels: false,
      },
      scales: {
        x: { grid: { display: false }, ticks: { font: { size: 11 } } },
        y: { grid: { display: false }, ticks: { font: { size: 11 } } },
      },
    },
  },

  line: {
    type: 'line',
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { position: 'top', labels: { usePointStyle: true, font: { size: 12 } } },
        datalabels: false,
      },
      scales: {
        x: { grid: { display: false }, ticks: { font: { size: 11 } } },
        y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.05)' }, ticks: { font: { size: 11 } } },
      },
      elements: {
        line: { tension: 0.3, borderWidth: 2 },
        point: { radius: 3, hoverRadius: 5 },
      },
    },
  },

  bar: {
    type: 'bar',
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        datalabels: false,
      },
      scales: {
        x: { grid: { display: false }, ticks: { font: { size: 11 } } },
        y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.05)' }, ticks: { font: { size: 11 } } },
      },
    },
  },
};

/**
 * Deep merge a preset with user config. Returns a new config object.
 */
export function withPreset(presetName, userConfig) {
  const preset = PRESETS[presetName];
  if (!preset) return userConfig;
  return deepMerge(deepClone(preset), userConfig);
}

function deepClone(obj) {
  if (obj === null || typeof obj !== 'object') return obj;
  if (typeof obj === 'function') return obj;
  if (Array.isArray(obj)) return obj.map(deepClone);
  const clone = {};
  for (const key of Object.keys(obj)) {
    clone[key] = typeof obj[key] === 'function' ? obj[key] : deepClone(obj[key]);
  }
  return clone;
}

function deepMerge(target, source) {
  for (const key of Object.keys(source)) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      if (!target[key]) target[key] = {};
      deepMerge(target[key], source[key]);
    } else {
      target[key] = source[key];
    }
  }
  return target;
}
